import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, IconButton, Divider, Stack } from '@mui/material';
import { Minus, Close, ReceiptLong, CreditCard } from '../ui/Icons';
import { formatPrice } from '../../utils/formatPrice';

// Group selected addons by their category so a line reads
// "Size: Large · Extras: Bacon, Cheese" instead of a flat comma list.
const formatAddons = (selectedAddons = []) => {
  const groups = new Map();
  selectedAddons.forEach((a) => {
    const cat = a.category || '';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(a.option);
  });
  return Array.from(groups.entries())
    .map(([cat, opts]) => (cat ? `${cat}: ${opts.join(', ')}` : opts.join(', ')))
    .join(' · ');
};

/**
 * Right rail: the order being built (unsent cart) for the active tab, the tab's
 * running totals, and the settle actions (charge / receipt / close).
 */
const CartPanel = ({
  cart = [],
  currency,
  activeTab,
  onChangeQty,
  onRemove,
  onSend,
  onCharge,
  onReceipt,
  onCloseTab,
  sending,
}) => {
  const { t } = useTranslation();

  if (!activeTab) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', textAlign: 'center', px: 2 }}>
        <ReceiptLong size={36} />
        <Typography sx={{ mt: 1.5, fontWeight: 600 }}>{t('pos.cart.noTabTitle', 'No tab selected')}</Typography>
        <Typography sx={{ fontSize: 14, mt: 0.5 }}>{t('pos.cart.noTabHint', 'Open or pick a table to start taking an order.')}</Typography>
      </Box>
    );
  }

  const fin = activeTab.financials || { total: 0, paid: 0, owed: 0, order_count: 0 };
  const fullyPaid = fin.owed <= 0 && fin.order_count > 0;
  const cartSubtotal = cart.reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
        {t('pos.tabs.table', 'Table')} {activeTab.table_number}
      </Typography>

      {/* Current (unsent) cart */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mt: 1.5 }}>
        {cart.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 2 }}>
            {t('pos.cart.empty', 'Tap items to build the next order.')}
          </Typography>
        ) : (
          cart.map((item, index) => (
            <Box key={item.uid != null ? item.uid : `${item.id}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{item.name}</Typography>
                {item.selectedAddons?.length > 0 && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {formatAddons(item.selectedAddons)}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                  {formatPrice(item.price, currency)}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton size="small" onClick={() => onChangeQty(index, -1)} aria-label={t('pos.menu.decrease', 'Decrease quantity')}>
                  <Minus size={14} />
                </IconButton>
                <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 700 }}>{item.quantity}</Typography>
                <IconButton size="small" onClick={() => onChangeQty(index, 1)} aria-label={t('pos.menu.increase', 'Increase quantity')}>
                  <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>+</Box>
                </IconButton>
                <IconButton size="small" onClick={() => onRemove(index)} aria-label={t('pos.cart.remove', 'Remove')}>
                  <Close size={14} />
                </IconButton>
              </Stack>
            </Box>
          ))
        )}
      </Box>

      {/* Send to kitchen */}
      {cart.length > 0 && (
        <Box sx={{ pt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{t('pos.cart.newOrderTotal', 'New order')}</Typography>
            <Typography sx={{ fontWeight: 700 }}>{formatPrice(cartSubtotal, currency)}</Typography>
          </Stack>
          <Button fullWidth variant="contained" onClick={onSend} disabled={sending}>
            {t('pos.cart.send', 'Send to kitchen')}
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Tab summary + settle */}
      <Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ color: 'text.secondary' }}>{t('pos.cart.tabTotal', 'Tab total')}</Typography>
          <Typography sx={{ fontWeight: 600 }}>{formatPrice(fin.total, currency)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ color: 'text.secondary' }}>{t('pos.paid', 'Paid')}</Typography>
          <Typography sx={{ fontWeight: 600 }}>{formatPrice(fin.paid, currency)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('pos.owed', 'Owed')}</Typography>
          <Typography sx={{ fontWeight: 800, color: fin.owed > 0 ? 'warning.dark' : 'success.main' }}>
            {formatPrice(fin.owed, currency)}
          </Typography>
        </Stack>

        {/* One dominant action per stage: Charge while a balance is owed, then
            Close tab once the tab is fully paid. Receipt stays low-emphasis. */}
        <Stack spacing={1}>
          <Button
            fullWidth
            variant={fin.owed > 0 ? 'contained' : 'outlined'}
            color="secondary"
            startIcon={<CreditCard size={18} />}
            onClick={onCharge}
            disabled={fin.owed <= 0}
          >
            {t('pos.cart.charge', 'Charge')} {fin.owed > 0 ? formatPrice(fin.owed, currency) : ''}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="text" onClick={onReceipt} disabled={fin.order_count === 0}>
              {t('pos.cart.receipt', 'Receipt')}
            </Button>
            <Button
              fullWidth
              variant={fullyPaid ? 'contained' : 'outlined'}
              color="success"
              onClick={onCloseTab}
              disabled={fin.owed > 0 || fin.order_count === 0}
            >
              {t('pos.cart.close', 'Close tab')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default CartPanel;
